import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type Dispatch,
    type RefObject,
    type SetStateAction,
} from "react";
import type { ColumnFilterOption, SortDirection } from "./ColumnFilterPopup";

export interface UseColumnFilterResult {
    /** 並び替え方向（null = 並び替えなし） */
    sort: SortDirection;
    setSort: Dispatch<SetStateAction<SortDirection>>;
    /** チェック中の値（初期化後は全件で埋まる） */
    selected: string[];
    setSelected: Dispatch<SetStateAction<string[]>>;
    /** ポップアップ表示状態 */
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    /** 初期化済みフラグ（チラつき防止用） */
    ready: boolean;
    /** カラムヘッダーのアンカー要素 */
    anchorRef: RefObject<HTMLElement | null>;
    /** チェックボックス選択肢（distinct） */
    options: ColumnFilterOption[];
    /** バッジ表示用の件数（全選択 or 未初期化なら undefined） */
    filterCount: number | undefined;
    /** このカラムの絞り込みが効いているか（true = 部分絞り込み中） */
    isFiltering: boolean;
    /** 選択値の有効性チェック（options に含まれていれば true） */
    isValueAllowed: (value: string) => boolean;
}

/**
 * カラム単位の並び替え/絞り込み状態を管理するフック。
 *
 * - items から distinct な選択肢を抽出
 * - 初回ロード時に「全選択」状態で初期化（= 絞り込みなし）
 * - バッジ件数・初期化済みフラグなど ColumnFilterPopup 連携に必要な値をまとめて返す
 *
 * @param items 元データ
 * @param getKey 各データから絞り込みキーを取り出す関数
 * @param getLabel キーを表示用ラベルへ変換（省略時はキーそのまま）
 */
export function useColumnFilter<T>(
    items: T[],
    getKey: (item: T) => string,
    getLabel?: (key: string) => string,
): UseColumnFilterResult {
    const [sort, setSort] = useState<SortDirection>(null);
    const [selected, setSelected] = useState<string[]>([]);
    const [open, setOpen] = useState(false);
    const [ready, setReady] = useState(false);
    /**
     * ユーザーが明示的に選択を変更したか。
     * options が非同期で変わる派生列で、内部初期化と新キー追加の隙間に
     * 「stale な selected」と「新しい options」がレンダリングされてバッジが
     * チラ見えするのを防ぐためのフラグ。ユーザー操作 = true、内部更新は触らない。
     */
    const [userModified, setUserModified] = useState(false);
    const anchorRef = useRef<HTMLElement | null>(null);
    /** 直前の options を保持（あとから追加されたキーを判定するため） */
    const prevOptionValuesRef = useRef<Set<string>>(new Set());

    // useMemo の依存に inline arrow を入れると毎回再計算されるため、ref で受ける
    const getKeyRef = useRef(getKey);
    const getLabelRef = useRef(getLabel);
    useEffect(() => {
        getKeyRef.current = getKey;
        getLabelRef.current = getLabel;
    });

    const options = useMemo<ColumnFilterOption[]>(() => {
        const seen = new Set<string>();
        const list: ColumnFilterOption[] = [];
        for (const item of items) {
            const key = getKeyRef.current(item);
            if (seen.has(key)) continue;
            seen.add(key);
            list.push({
                value: key,
                label: getLabelRef.current?.(key) ?? key,
            });
        }
        list.sort((a, b) => a.label.localeCompare(b.label, "ja"));
        return list;
    }, [items]);

    // 初回データ到着時に全選択スタート。
    // 既に初期化済みでも、後から options に追加されたキーは selected に取り込む（非同期データ対応）。
    useEffect(() => {
        if (options.length === 0) {
            prevOptionValuesRef.current = new Set();
            return;
        }
        if (!ready) {
            setSelected(options.map((o) => o.value));
            setReady(true);
            prevOptionValuesRef.current = new Set(options.map((o) => o.value));
            return;
        }
        const prevSet = prevOptionValuesRef.current;
        const newKeys = options.filter((o) => !prevSet.has(o.value)).map((o) => o.value);
        if (newKeys.length > 0) {
            setSelected((prev) => [...prev, ...newKeys]);
        }
        prevOptionValuesRef.current = new Set(options.map((o) => o.value));
    }, [options, ready]);

    // options に存在する選択値だけをカウント（ステイル値は無視）
    const optionValueSet = useMemo(
        () => new Set(options.map((o) => o.value)),
        [options],
    );
    const validSelectedCount = useMemo(
        () => selected.reduce((n, v) => (optionValueSet.has(v) ? n + 1 : n), 0),
        [selected, optionValueSet],
    );

    const filterCount =
        !ready || !userModified || validSelectedCount === options.length
            ? undefined
            : validSelectedCount;
    const isFiltering =
        ready && userModified && validSelectedCount < options.length;

    const isValueAllowed = useCallback(
        (value: string) => optionValueSet.has(value),
        [optionValueSet],
    );

    /** 外部公開用 setSelected。呼ばれたら userModified を true にする */
    const setSelectedByUser = useCallback<Dispatch<SetStateAction<string[]>>>(
        (value) => {
            setUserModified(true);
            setSelected(value);
        },
        [],
    );

    return {
        sort,
        setSort,
        selected,
        setSelected: setSelectedByUser,
        open,
        setOpen,
        ready,
        anchorRef,
        options,
        filterCount,
        isFiltering,
        isValueAllowed,
    };
}
